import React from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'react-bootstrap';
import { Control, FieldErrors, useForm } from 'react-hook-form';

import { ICreateWorkerFormData } from 'forms/worker';
import { HelpIcon } from 'components/shared/HelpIcon/HelpIcon';
import { GenderController } from 'components/shared/GenderController/GenderController';
import { DateController } from 'components/shared/DateController/DateController';

interface ICreateWorkerWorkerDetailsProps {
    register: ReturnType<typeof useForm>['register'];
    errors: FieldErrors<ICreateWorkerFormData>;
    control: Control;
    disabled?: boolean;
    loadRecaptcha: () => void;
}

export const CreateWorkerWorkerDetails = (
    {register, errors, control, disabled, loadRecaptcha}: ICreateWorkerWorkerDetailsProps
) => {
    const {t} = useTranslation();

    return (
        <div className="CreateWorker__form-section">
            <div className="form__section-header">
                <Form.Label className="form-label--title">{t('common.personalDetails', 'Personal Details')}</Form.Label>
            </div>
            <Row>
                <Col sm={6}>
                    <Form.Group controlId="firstName">
                        <Form.Label className="form-label--smaller">
                            {t('common.firstName', 'First Name')}
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={t('common.firstName', 'First Name')}
                            size="lg"
                            name="firstName"
                            ref={register()}
                            isInvalid={!!errors?.firstName}
                            disabled={disabled}
                            autoFocus
                            onClick={() => loadRecaptcha()}
                        />
                        {(errors && errors.firstName) && (
                            <Form.Control.Feedback type="invalid">
                                {t('errors.firstName.missing', 'You must enter a valid first name')}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
                <Col sm={6}>
                    <Form.Group controlId="middleName">
                        <Form.Label className="form-label--smaller">
                            {t('common.middleNameOptional', 'Middle Name (optional)')}
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={t('common.middleName', 'Middle Name')}
                            size="lg"
                            name="middleName"
                            ref={register()}
                            isInvalid={!!errors?.middleName}
                            disabled={disabled}
                            onClick={() => loadRecaptcha()}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Form.Group controlId="lastName">
                <Form.Label className="form-label--smaller">
                    {t('common.lastName', 'Last Name')}
                </Form.Label>
                <Form.Control
                    size="lg"
                    type="text"
                    placeholder={t('common.lastName', 'Last Name')}
                    name="lastName"
                    ref={register()}
                    isInvalid={!!errors?.lastName}
                    disabled={disabled}
                    onClick={() => loadRecaptcha()}
                />
                {(errors && errors.lastName) && (
                    <Form.Control.Feedback type="invalid">
                        {t('errors.lastName.missing', 'You must enter a valid last name')}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group controlId="email">
                <Form.Label className="form-label--smaller">
                    {t('common.personalEmailAddress', 'Personal Email Address')}
                    <HelpIcon id="CreateWorker__email" className="ml-2">
                        <Trans i18nKey="createWorker.tooltips.email">
                            Your personal email address. This email address must be unique, and must be an
                            actual verifiable email address. This field is required.
                        </Trans>
                    </HelpIcon>
                </Form.Label>
                <Form.Control
                    size="lg"
                    type="email"
                    placeholder={t('common.personalEmailAddress', 'Personal Email Address')}
                    name="email"
                    ref={register()}
                    isInvalid={!!errors?.email}
                    disabled={disabled}
                    onClick={() => loadRecaptcha()}
                />
                {(errors && errors.email) && (
                    <Form.Control.Feedback type="invalid">
                        {t('errors.personalEmail.missing', 'You must enter a valid personal email address')}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group controlId="confirmEmail">
                <Form.Label className="form-label--smaller">
                    {t('common.confirmPersonalEmailAddress', 'Confirm Personal Email Address')}
                    <HelpIcon id="CreateWorker__email" className="ml-2">
                        <Trans i18nKey="createWorker.tooltips.confirmEmail">
                            Please confirm your email address as entered above. This must be an exact match.
                        </Trans>
                    </HelpIcon>
                </Form.Label>
                <Form.Control
                    size="lg"
                    type="email"
                    placeholder={t('common.confirmEmailAddress', 'Confirm Email Address')}
                    name="confirmEmail"
                    ref={register()}
                    isInvalid={!!errors?.confirmEmail}
                    disabled={disabled}
                    onClick={() => loadRecaptcha()}
                />
                {(errors?.confirmEmail) && (
                    <Form.Control.Feedback type="invalid">
                        {t('errors.personalEmail.doesNotMatch', 'Your email addresses do not match.')}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group controlId="phone">
                <Form.Label className="form-label--smaller">
                    {t('common.mobileNumber', 'Mobile Number')}
                    <HelpIcon id="PhoneNumberControl__controlId.description" className="ml-2">
                        <Trans i18nKey="createWorker.tooltips.phone">
                            Your mobile phone number. This field is required.
                        </Trans>
                    </HelpIcon>
                </Form.Label>
                <Form.Control
                    size="lg"
                    type="text"
                    placeholder={t('common.mobileNumber', 'Mobile Number')}
                    name="phone"
                    ref={register()}
                    isInvalid={!!errors?.phone}
                    disabled={disabled}
                    onClick={() => loadRecaptcha()}
                />
                {(errors && errors.phone) && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                        {t('errors.mobileNumber.missing', 'You must enter a valid mobile number')}
                    </Form.Control.Feedback>
                )}
            </Form.Group>

            <Form.Group controlId="gender">
                <Form.Label className="form-label--smaller">
                    {t('common.gender', 'Gender')}
                </Form.Label>
                <GenderController
                    name="gender"
                    size="lg"
                    control={control}
                    disabled={disabled}
                />
                {(errors && errors.gender) && (
                    <Form.Control.Feedback type="invalid">
                        {t('errors.gender.missing', 'You must select a gender')}
                    </Form.Control.Feedback>
                )}
            </Form.Group>
            <Form.Group controlId="dob">
                <Form.Label className="form-label--smaller">
                    {t('common.dateOfBirth', 'Date of Birth')}
                </Form.Label>
                <DateController name="dob" control={control} size="lg" disabled={disabled}/>
                {(errors && errors.dob) && (
                    <Form.Control.Feedback type="invalid" className="d-block">
                        {t('errors.dateOfBirth.missing', 'You must enter a valid date of birth')}
                    </Form.Control.Feedback>
                )}
            </Form.Group>
        </div>
    );
};
